// Mouse-leave exit-intent popup offering a discount code on bookings/checkout pages.
// Triggers ONCE per session when the cursor leaves through the top of the viewport.
import { useEffect, useRef, useState } from "react";
import { X, Sparkle, CheckCircle } from "@phosphor-icons/react";

const KEY = "pp_exit_intent_shown";

export default function ExitIntentPopup({ code = "PUPPY15", percent = 15 }) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const autoCloseRef = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem(KEY)) return;
    const onLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem(KEY)) {
        setShow(true);
        sessionStorage.setItem(KEY, "1");
      }
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, []);

  // Tidy any pending auto-close timer when the popup unmounts.
  useEffect(() => () => { if (autoCloseRef.current) clearTimeout(autoCloseRef.current); }, []);

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(code);
    } catch {
      // Some browsers (older Safari, insecure contexts) reject clipboard
      // writes — fall back to a hidden textarea + execCommand.
      try {
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        /* if it still fails the user can read the code on screen */
      }
    }
    setCopied(true);
    if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    autoCloseRef.current = setTimeout(() => setShow(false), 1600);
  };

  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-stone-900/60 z-[70] flex items-center justify-center p-4" data-testid="exit-intent-popup">
      <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden">
        <div className="bg-gradient-to-br from-[#f38838] to-[#d97324] text-white p-6 text-center relative">
          <button onClick={() => setShow(false)} className="absolute top-3 right-3 text-white/80 hover:text-white" data-testid="exit-intent-close" aria-label="Close"><X size={20} /></button>
          <Sparkle size={32} weight="duotone" className="mx-auto mb-2" />
          <div className="text-xs uppercase tracking-wider font-bold opacity-80">Before you go…</div>
          <h2 className="font-heading text-3xl font-bold mt-1">Save {percent}%</h2>
        </div>
        <div className="p-6 text-center">
          <p className="text-stone-700 mb-4">Use this code at checkout to take <strong>{percent}% off</strong> your first puppy class.</p>
          <div className="bg-stone-100 rounded-lg p-3 font-mono font-bold text-lg text-stone-900 tracking-wider mb-3" data-testid="exit-intent-code">{code}</div>
          {copied ? (
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-4 py-2 text-sm font-semibold" data-testid="exit-intent-copied" role="status" aria-live="polite">
              <CheckCircle size={18} weight="fill" className="text-emerald-600" />
              Copied — safe to exit
            </div>
          ) : (
            <button onClick={copy} className="btn-secondary text-sm" data-testid="exit-intent-copy">Copy code</button>
          )}
        </div>
      </div>
    </div>
  );
}
